import { useState } from 'react'
import type { Place } from '../../shared/places'
import type { Point } from '../lib/geo'
import { useI18n } from '../lib/i18n'
import { PlacePicker } from './PlacePicker'

export const MIN_KM = 1
export const MAX_KM = 60

interface Props {
  origin: Point | null
  /** Set when the origin came from the place list rather than the browser. */
  originName?: string
  radiusKm: number
  /** How many clubs the current radius selects, for immediate feedback. */
  matches: number
  onLocate: (origin: Point) => void
  onPlace: (place: Place) => void
  onRadius: (km: number) => void
  onClear: () => void
}

type Status = 'idle' | 'locating' | 'denied' | 'unavailable'

export function NearMe({
  origin,
  originName,
  radiusKm,
  matches,
  onLocate,
  onPlace,
  onRadius,
  onClear,
}: Props) {
  const { t } = useI18n()
  const [status, setStatus] = useState<Status>('idle')

  const locate = () => {
    if (!navigator.geolocation) return setStatus('unavailable')
    setStatus('locating')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setStatus('idle')
        onLocate({ lat: pos.coords.latitude, lon: pos.coords.longitude })
      },
      (err) => setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable'),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    )
  }

  if (!origin) {
    const failed = status === 'denied' || status === 'unavailable'
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={locate}
            disabled={status === 'locating'}
            className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60"
            style={{ borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
          >
            <TargetIcon />
            {status === 'locating' ? t.locating : t.nearMe}
          </button>
          <span className="text-[11px]" style={{ color: 'var(--text-faint)' }}>
            {t.orPickPlace}
          </span>
          {/* Focused automatically once geolocation has failed, since the
              picker is then the only way forward. */}
          <PlacePicker onPick={onPlace} autoFocus={failed} />
        </div>
        {failed && (
          <span className="text-[11px]" style={{ color: 'var(--text-faint)' }}>
            {status === 'denied' ? t.locationBlocked : t.locationUnavailable}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <div className="flex items-center gap-2">
        <TargetIcon on />
        <label htmlFor="radius" className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {t.within}
        </label>
        <span className="nums text-xs font-semibold" style={{ minWidth: '3.5rem' }}>
          {radiusKm} km
        </span>
      </div>

      <input
        id="radius"
        type="range"
        min={MIN_KM}
        max={MAX_KM}
        step={1}
        value={radiusKm}
        onChange={(e) => onRadius(Number(e.target.value))}
        className="h-1.5 min-w-[9rem] flex-1 cursor-pointer appearance-none rounded-full"
        style={{
          background: `linear-gradient(to right, var(--color-brand-400) ${
            ((radiusKm - MIN_KM) / (MAX_KM - MIN_KM)) * 100
          }%, var(--border-strong) 0%)`,
        }}
        aria-label={t.distanceLabel}
      />

      <span className="text-[11px]" style={{ color: 'var(--text-faint)' }}>
        <span className="nums">{t.clubs(matches)}</span>
        {originName ? ` ${t.fromPlace(originName)}` : ''}
      </span>

      <button
        type="button"
        onClick={onClear}
        className="text-[11px] underline underline-offset-2"
        style={{ color: 'var(--text-faint)' }}
      >
        {t.clear}
      </button>
    </div>
  )
}

function TargetIcon({ on = false }: { on?: boolean }) {
  const colour = on ? 'var(--color-brand-400)' : 'currentColor'
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="3" stroke={colour} strokeWidth="1.6" />
      <circle cx="8" cy="8" r="6.2" stroke={colour} strokeWidth="1.2" opacity="0.5" />
      <path d="M8 0v2M8 14v2M0 8h2M14 8h2" stroke={colour} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
